import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';

const SEO = ({ title, description, schema = 'Organization', lang: langProp, schemaData = null }) => {
  const { t } = useTranslation();
  const { lang: urlLang } = useParams();
  const location = useLocation();
  const lang = langProp || urlLang || 'en';
  
  const baseUrl = "https://npuniverse.com";
  const currentPath = location.pathname || `/${lang}`;
  const currentUrl = `${baseUrl}${currentPath}`;
  
  const seoTitle = title || t('seo.home_title');
  const seoDesc = description || t('seo.home_desc');

  // Schema definitions
  const schemas = {
    Organization: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "NPU Universe",
      "url": baseUrl,
      "logo": `${baseUrl}/assets/logo.png`,
      "sameAs": [
        "https://github.com/Ericson246/NPU-Agent",
        "https://github.com/Ericson246/npu-optimize"
      ],
      "description": "The Private Local AI Ecosystem - Free benchmarks and AI apps that run 100% on your device."
    },
    WebApplication: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "AI Benchmark by NPU Universe",
      "url": `${baseUrl}/${lang}/benchmark`,
      "description": "Test your device's AI performance in tokens per second. Compare CPU, GPU, and NPU speed online for free.",
      "applicationCategory": "UtilitiesApplication",
      "operatingSystem": "All",
      "browserRequirements": "Requires WebGPU or WebAssembly support",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    },
    SoftwareApplication: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NPU Check",
      "operatingSystem": "Android, Web, Windows",
      "applicationCategory": "DeveloperApplication, UtilityApplication",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": t('features.npu_check.tagline'),
      "keywords": "NPU, CPU, GPU, AI, Benchmark, Offline AI, Android NPU, WebGPU"
    }
  };

  const currentSchema = schemaData || schemas[schema] || schemas.Organization;

  // Breadcrumb Logic
  const pathParts = urlLang ? location.pathname.split('/').filter(p => p && p !== urlLang) : [];
  const breadcrumbs = [
    { name: 'Home', item: baseUrl },
    ...pathParts.map((part, index) => ({
      name: part.charAt(0).toUpperCase() + part.slice(1),
      item: `${baseUrl}/${lang}/${pathParts.slice(0, index + 1).join('/')}`
    }))
  ];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDesc} />
      <link rel="canonical" href={currentUrl} />
      
      {/* Social SEO (Open Graph) */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDesc} />
      <meta property="og:image" content={`${baseUrl}/assets/banner.jpg`} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDesc} />
      <meta name="twitter:image" content={`${baseUrl}/assets/banner.jpg`} />
      
      {/* Hreflang Tags */}
      <link rel="alternate" hreflang="en" href={`${baseUrl}${currentPath.replace(/^\/es/, '/en')}`} />
      <link rel="alternate" hreflang="es" href={`${baseUrl}${currentPath.replace(/^\/en/, '/es')}`} />
      <link rel="alternate" hreflang="x-default" href={`${baseUrl}${currentPath.replace(/^\/(en|es)/, '/en')}`} />

      {/* Dynamic Schema based on page type */}
      <script type="application/ld+json">
        {JSON.stringify(currentSchema)}
      </script>

      {/* Breadcrumb Schema - always included */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": b.name,
            "item": b.item
          }))
        })}
      </script>
    </Helmet>
  );
};

export default SEO;

